import type { CanvasFeature } from '@pn/core/domain/drawing';
import { hasKey, hasKeyWithType } from '@pn/core/utils/logic';
import { isArray, isEmpty, isNil, isObject, isString } from 'lodash-es';

export type Layer = {
  id: string;
  name?: string;
  type: LayerType;
  style: LayerStyle;
  renderAsPoints?: boolean;
  source: GeoJSONSource | VectorSource | MediaSource;
  sourceLayer: string | undefined;
  sourceField?: string;
  metadata?:
    | {
        type: 'drawing';
        features: CanvasFeature[];
      }
    | {
        type: 'crosshatch' | 'hatch_diag45' | 'hatch_diag135';
        tileSize: number;
        lineWidth: number;
      }
    | {
        type: 'dots';
        tileSize: number;
        dotRadius: number;
      };
};

type GeoJSONSource = {
  type: 'geojson';
  data: string | GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry;
};
type VectorSource =
  | {
      type: 'vector';
      url: string;
    }
  | {
      type: 'vector';
      tiles: string[];
      minzoom?: number;
      maxzoom?: number;
    };
type MediaSource = {
  type: 'raster' | 'raster-dem' | 'image' | 'video';
  [key: string]: unknown;
};
export function isLayerSource(arg: unknown): arg is Layer['source'] {
  return (
    isObject(arg) &&
    hasKeyWithType(arg, 'type', isString) &&
    ['geojson', 'vector', 'raster', 'raster-dem', 'image', 'video'].includes(
      arg.type
    ) &&
    (arg.type === 'geojson' ? hasKey(arg, 'data') && !isNil(arg.data) : true)
  );
}

export type CustomSVGIconData = {
  pathData: string[];
  viewBox?: string;
};

export type FilterProperty = {
  field: string;
  options: Record<
    string,
    {
      label: string;
      icon?: CustomSVGIconData;
      value: boolean;
    }
  >;
};

export enum LayerType {
  Icon = 'icon', // symbol in Mapbox
  Text = 'text', // symbol in Mapbox
  Line = 'line',
  Polygon = 'polygon', // fill in Mapbox
  Circle = 'circle',
  Raster = 'raster',
  Background = 'background',
}

export function formatLayerType(
  type: LayerType,
  options?: { plural?: boolean }
): string {
  const { plural = false } = options ?? {};

  switch (type) {
    case LayerType.Icon:
      return plural ? 'Icons' : 'Icon';
    case LayerType.Text:
      return 'Text';
    case LayerType.Line:
      return plural ? 'Lines' : 'Line';
    case LayerType.Polygon:
      return plural ? 'Polygons' : 'Line';
    case LayerType.Circle:
      return plural ? 'Circles' : 'Circle';
    case LayerType.Raster:
      return 'Raster';
    case LayerType.Background:
      return 'Background';
  }
}

export function isInteractableLayer(layer: Layer): boolean {
  return ![LayerType.Raster, LayerType.Background].includes(layer.type);
}

export function isCustomPatternLayer(layer: Layer): layer is Layer & {
  metadata:
    | {
        type: 'crosshatch' | 'hatch_diag45' | 'hatch_diag135';
        tileSize: number;
        lineWidth: number;
      }
    | { type: 'dots'; tileSize: number; dotRadius: number };
} {
  if (isNil(layer.metadata)) return false;
  return layer.metadata.type !== 'drawing';
}

export const layerPatterns = [
  'crosshatch',
  'hatch_diag45',
  'hatch_diag135',
  'dots',
] as const;
export type LayerPattern = (typeof layerPatterns)[number];

export function createLayerPatternMetadata(
  type: LayerPattern
): Layer['metadata'] {
  switch (type) {
    case 'crosshatch':
      return { type, tileSize: 12, lineWidth: 1.5 };
    case 'hatch_diag45':
      return { type, tileSize: 12, lineWidth: 1.5 };
    case 'hatch_diag135':
      return { type, tileSize: 12, lineWidth: 1.5 };
    case 'dots':
      return { type, tileSize: 6, dotRadius: 1.5 };
  }
}

export type AnnotationFeature = GeoJSON.Feature<
  GeoJSON.Geometry,
  {
    lineColor?: string;
    lineWidth?: number;
    fillColor?: string;
    fillOpacity?: number;
    textField?: string;
    textSize?: number;
    textColor?: string;
  }
>;

type AnnotationSource = Omit<GeoJSONSource, 'data'> & {
  data: GeoJSON.FeatureCollection<
    GeoJSON.Geometry,
    AnnotationFeature['properties']
  >;
};
export function isAnnotationSource(arg: unknown): arg is AnnotationSource {
  return (
    isObject(arg) &&
    hasKey(arg, 'type') &&
    arg.type === 'geojson' &&
    hasKeyWithType(arg, 'data', isObject) &&
    hasKey(arg.data, 'type') &&
    arg.data.type === 'FeatureCollection' &&
    hasKeyWithType(arg.data, 'features', isArray)
  );
}

const layerStyleProperties = [
  'color',
  'opacity',
  'size',
  'radius',
  'width',
  'maxWidth',
  'dashArray',
  'lineCap',
  'lineJoin',
  'outlineColor',
  'strokeColor',
  'strokeWidth',
  'strokeOpacity',
  'placement',
  'lineHeight',
  'haloColor',
  'haloWidth',
  'haloBlur',
  'padding',
  'image',
  'field',
  'font',
  'anchor',
  'offset',
  'allowOverlap',
  'ignorePlacement',
  'symbolSortKey',
  'symbolZOrder',
] as const;
export type LayerStyleProperty = (typeof layerStyleProperties)[number];
export function isLayerStyleProperty(key: string): key is LayerStyleProperty {
  return layerStyleProperties.includes(key as LayerStyleProperty);
}

export type LayerStyle = Partial<Record<LayerStyleProperty, unknown>>;

type ValueAndColor = { value: string; color: string };
type ColorMapping = unknown[] | string;

export function generateColorMappingByProperty(
  valuesAndColors: ValueAndColor[],
  propertyGetter: unknown[], // e.g. ['get', property]
  defaultColor = '#000'
): ColorMapping {
  if (isEmpty(valuesAndColors)) return defaultColor;

  return [
    'match',
    propertyGetter,
    ...valuesAndColors.flatMap(({ value, color }) => [value, color]), // pairs of value and corresponding color
    defaultColor,
  ];
}
